class BundleVariantSelector {
  constructor(component) {
    this.component = component;
    this.productData = JSON.parse(component.dataset.productJson);

    // Add null check for component
    if (!this.component) {
      return;
    }

    this.radios = this.component.querySelectorAll('.bundle-variant-radio');
    this.image = this.component.querySelector('.bundle-component-image img');

    this.bindEvents();
    this.setAvailableOptions();
    this.updateDesignatedColorSpans(); // Ensure designated-color is set on load
  }

  bindEvents() {
    // Add null check for radios
    if (!this.radios || this.radios.length === 0) {
      return;
    }

    this.radios.forEach(radio => {
      radio.addEventListener('change', () => {
        this.updateVariant();
        this.setAvailableOptions();
        updateBundlePricing();
        document.dispatchEvent(new CustomEvent('bundle:variant-change'));
      });
    });
  }

  updateVariant() {
    // Add null check for component
    if (!this.component) return;

    const selectedOptions = this.getSelectedOptions();

    const matchingVariant = this.productData.variants.find(variant =>
      variant.options.every((opt, idx) => opt === selectedOptions[idx])
    );

    if (matchingVariant) {
      this.component.dataset.variantId = matchingVariant.id;
    } else {
      delete this.component.dataset.variantId;
    }

    // Update image from selected color swatch's data-featured-image, if present
    const colorFieldset = this.component.querySelector('fieldset.bundle-colors');
    if (colorFieldset) {
      const selectedColorRadio = colorFieldset.querySelector('.bundle-variant-radio:checked');
      if (selectedColorRadio && selectedColorRadio.dataset.featuredImage && this.image) {
        this.image.src = selectedColorRadio.dataset.featuredImage;
      }
      // If no data-featured-image, do not change the image
    }

    this.updateDesignatedColorSpans();
  }

  /**
   * Updates the .designated-color span for each color option to reflect the currently selected value.
   */
  updateDesignatedColorSpans() {
    const optionGroups = this.component.querySelectorAll('fieldset');
    optionGroups.forEach((group) => {
      if (group.classList.contains('bundle-colors')) {
        const selectedRadio = group.querySelector('.bundle-variant-radio:checked');
        if (selectedRadio) {
          const optionLabel = group.previousElementSibling;
          if (optionLabel) {
            const designatedColor = optionLabel.querySelector('.designated-color');
            if (designatedColor) {
              designatedColor.textContent = selectedRadio.value;
            }
          }
        }
      }
    });
  }

  getSelectedOptions() {
    // Add null check for component
    if (!this.component) return [];

    return Array.from(this.component.querySelectorAll('.bundle-variant-radio:checked')).map(input => input.value);
  }

  setAvailableOptions() {
    // Add null check for component
    if (!this.component) return;

    const optionGroups = Array.from(this.component.querySelectorAll('fieldset'));

    optionGroups.forEach((group, optionIndex) => {
      const radios = group.querySelectorAll('.bundle-variant-radio');

      if (optionIndex === 0) {
        // First group: all available options are enabled, preselect first available if none selected
        let foundSelected = false;
        radios.forEach(radio => {
          const isAvailable = this.productData.variants.some(variant => {
            return variant.available && variant.options[0] === radio.value;
          });
          radio.disabled = !isAvailable;
          const label = this.component.querySelector(`label[for="${radio.id}"]`);
          if (label) {
            label.classList.toggle('unavailable', !isAvailable);
          }
          if (isAvailable && radio.checked) {
            foundSelected = true;
          }
        });
        // If none selected, select the first available
        if (!foundSelected) {
          for (const radio of radios) {
            if (!radio.disabled) {
              radio.checked = true;
              break;
            }
          }
        }
      } else {
        // Other groups: update enabled/disabled state based on first selection, do not auto-select
        const firstSelected = this.getSelectedOptions()[0];
        radios.forEach(radio => {
          const isAvailable = this.productData.variants.some(variant => {
            return variant.available && variant.options[0] === firstSelected && variant.options[optionIndex] === radio.value;
          });
          radio.disabled = !isAvailable;
          const label = this.component.querySelector(`label[for="${radio.id}"]`);
          if (label) {
            label.classList.toggle('unavailable', !isAvailable);
          }
        });
      }
    });
  }


}

/**
 * Global function to update bundle pricing based on selected variants
 */
function updateBundlePricing() {
  const bundleSelector = document.querySelector('.bundle-selector');
  if (!bundleSelector) return;

  const bundleDiscount = parseInt(bundleSelector.dataset.bundleDiscount) || 0;
  const discountMultiplier = bundleDiscount / 100;

  let totalCompareAtPrice = 0;
  let totalActualPrice = 0;

  // Calculate totals from all bundle components
  const allBundleComponents = document.querySelectorAll('.bundle-component');
  const selectedVariantIds = [];

    allBundleComponents.forEach(component => {
    const variantId = component.dataset.variantId;
    if (variantId) {
      selectedVariantIds.push(variantId);

      const productData = JSON.parse(component.dataset.productJson);
      const variant = productData.variants.find(v => v.id.toString() === variantId);

      if (variant) {
        const compareAtPrice = variant.compare_at_price || variant.price;
        const actualPrice = variant.price;

        totalCompareAtPrice += compareAtPrice;
        totalActualPrice += actualPrice;
      }
    }
  });

  // Calculate final price with discount
  const discountAmount = totalActualPrice * discountMultiplier;
  const finalPrice = totalActualPrice - discountAmount;

  // Only update pricing if all variants are selected
  if (selectedVariantIds.length !== allBundleComponents.length) {
    return;
  }

  // Update price display
  const bundlePriceContainer = document.querySelector('.bundle-price .price');

  if (bundlePriceContainer) {
    const compareAtElement = bundlePriceContainer.querySelector('s');
    const salePriceElement = bundlePriceContainer.querySelector('strong');
    if (compareAtElement) {
      compareAtElement.textContent = formatMoney(totalCompareAtPrice);
    }
    if (salePriceElement) {
      salePriceElement.textContent = formatMoney(finalPrice);
    }

    // Update price classes for sale styling
    const bundlePriceWrapper = document.querySelector('.bundle-price');
    if (bundlePriceWrapper) {
      bundlePriceWrapper.classList.toggle('price--on-sale', totalCompareAtPrice > finalPrice);
    }
  }
}

/**
 * Formats a price value to currency format
 * @param {number} price - Price in cents
 * @returns {string} Formatted price string
 */
function formatMoney(price) {
  // Convert cents to dollars and format
  const dollars = price / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(dollars);
}

document.addEventListener('DOMContentLoaded', () => {
  const bundleComponents = document.querySelectorAll('.bundle-component');
  const atcButton = document.querySelector('.bundle-atc');

  // Add null check for atcButton
  if (!atcButton) {
    return;
  }

  bundleComponents.forEach(component => new BundleVariantSelector(component));

  // Trigger initial state check to ensure ATC button is disabled until all selections are made
  document.dispatchEvent(new CustomEvent('bundle:variant-change'));

  document.addEventListener('bundle:variant-change', () => {
    const allComponents = document.querySelectorAll('.bundle-component');
    const selectedVariantIds = Array.from(allComponents)
      .map(el => el.dataset.variantId)
      .filter(Boolean);

    atcButton.setAttribute('data-variants-selected', selectedVariantIds.join(','));

    const allSelected = selectedVariantIds.length === allComponents.length;

    atcButton.disabled = !allSelected;
    const buttonSpan = atcButton.querySelector('span');
    if (buttonSpan) {
      buttonSpan.textContent = allSelected
        ? 'Add Bundle to Cart'
        : 'Select Bundle Items';
    }

    // Update bundle pricing when variants change
    updateBundlePricing();
  });

  atcButton.addEventListener('click', async (e) => {
    e.preventDefault();

    // Add loading state
    atcButton.setAttribute('aria-disabled', true);
    atcButton.classList.add('loading');
    const spinner = atcButton.querySelector('.loading__spinner');
    if (spinner) spinner.classList.remove('hidden');

    const bundleWrapper = document.querySelector('.bundle-selector');
    // Add null check for bundleWrapper
    if (!bundleWrapper) {
      return;
    }

    let dataBundleID = bundleWrapper.dataset.bundleId.split('gid://shopify/Metaobject/');
    let parsedBundleId = dataBundleID[1];

    const bundleName = bundleWrapper.dataset.bundleName;
    const bundleUrl = bundleWrapper.dataset.bundleUrl;
    const bundleDiscount = parseInt(bundleWrapper.dataset.bundleDiscount);
    const bundleId = parseInt(bundleWrapper.dataset.bundleId);
    const bundleEncryption = bundleWrapper.dataset.bundleEncryption;
    const bundleSeparator = Math.floor(Date.now() / 1000);

    const items = [];

    bundleComponents.forEach((component) => {
      const variantId = parseInt(component.dataset.variantId);
      if (!variantId) return;

      items.push({
        id: variantId,
        quantity: 1,
        properties: {
          "Included In": bundleName,
          "_bundle_item": true,
          "_bundle_id": parsedBundleId,
          "_bundle_discount": bundleDiscount,
          "_bundle_name": bundleName,
          "_bundle_url": bundleUrl,
          "_bundle_enctryption": bundleEncryption,
          "_bundle_separator": bundleSeparator
        }
      });
    });

    if (items.length !== bundleComponents.length) {
      alert('Please select all bundle items.');
      return;
    }

    const formData = new FormData();
    items.forEach((item, index) => {
      formData.append(`items[${index}][id]`, item.id);
      formData.append(`items[${index}][quantity]`, item.quantity);
      for (const [key, value] of Object.entries(item.properties)) {
        formData.append(`items[${index}][properties][${key}]`, value);
      }
    });

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      formData.append('sections', cartDrawer.getSectionsToRender().map(s => s.id));
      formData.append('sections_url', window.location.pathname);
      cartDrawer.setActiveElement(document.activeElement);
    }

    config.body = formData;

    const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');

    fetch(`${routes.cart_add_url}`, config)
      .then((res) => res.json())
      .then((response) => {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'bundle-atc',
            errors: response.errors || response.description,
            message: response.message,
          });
          alert(response.message || 'There was a problem adding your bundle to the cart.');
          return;
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'bundle-atc',
          cartData: response
        }).then(() => {
          CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
        });

        if (cartDrawer) {
          CartPerformance.measure('add:paint-updated-sections', () => {
            // Handle empty cart to non-empty transition
            if (response.items.length > 0) {
              // Remove empty state from cart drawer
              cartDrawer.classList.remove('is-empty');

              // Also remove empty state from cart-drawer-items if it exists
              const cartDrawerItems = cartDrawer.querySelector('cart-drawer-items');
              if (cartDrawerItems) {
                cartDrawerItems.classList.remove('is-empty');
              }
            }

            cartDrawer.renderContents(response);
          });
        }
         else {
          window.location = window.routes.cart_url;
        }

      })
      .catch((err) => {
        console.error('Error adding bundle:', err);
        alert('There was a problem adding your bundle to the cart.');
      })
      .finally(() => {
        // Remove loading state
        atcButton.removeAttribute('aria-disabled');
        atcButton.classList.remove('loading');
        if (spinner) spinner.classList.add('hidden');
      });
  });
});
